<ion-content>
  <div class="constrained-content">
    <ion-grid class="constrained-content">
      <ion-row>
        <ion-col size-md="8" size="12" offset-md="2">
          <!-- Show loading state while initially loading -->
          <div *ngIf="isInitialLoading" class="loading-state">
            <ion-card class="outline-card loading-card">
              <ion-card-content>
                <ion-spinner
                  name="crescent"
                  class="loading-spinner"
                ></ion-spinner>
                <h2>Loading portal information...</h2>
                <p class="loading-text">
                  Please wait while we check your portal configuration.
                </p>
              </ion-card-content>
            </ion-card>
          </div>

          <!-- Show CTA when no projects exist for this portal type -->
          <div
            *ngIf="!isInitialLoading && !hasPortal && !isCreatingPortal && hasNoProjectsForPortalType && authService.isTenantAdmin"
            class="no-portal-message"
          >
            <div class="no-portal-container">
              <div class="no-portal-content">
                <ion-card class="outline-card cta-card">
                  <ion-card-content>
                    <ion-icon name="folder-open-outline" class="cta-icon"></ion-icon>
                    <h3>No {{ projectTypeName }} projects found</h3>
                    <p>
                      You need to create an {{ projectTypeName }} project before you can create an {{ projectTypeName }} portal. 
                      When creating the project, make sure to set it as an {{ projectTypeName }} project.
                    </p>
                    <ion-button
                      (click)="navigateToProjectCreation()"
                      fill="outline"
                      class="cta-button"
                    >
                      <ion-icon name="add-outline"></ion-icon>
                      <span>Create {{ projectTypeName | titlecase }} Project</span>
                    </ion-button>
                  </ion-card-content>
                </ion-card>
              </div>
            </div>
          </div>

          <!-- Show message and create button when no portal exists but projects are available -->
          <div
            *ngIf="!isInitialLoading && !hasPortal && !isCreatingPortal && !hasNoProjectsForPortalType"
            class="no-portal-message"
          >
            <div class="no-portal-container">
              <div class="no-portal-content">
                <ion-card class="outline-card loading-card">
                  <ion-card-content>
                    <ion-icon
                      name="folder-open-outline"
                      class="no-portal-icon"
                    ></ion-icon>
                    <h2>{{ config.noPortalMessage }}</h2>
                    <p class="loading-description">
                      {{ config.noPortalDescription }}
                    </p>
                    <ion-button
                      (click)="startCreatePortal()"
                      *ngIf="authService.isTenantAdmin"
                    >
                      <ion-icon name="add-outline"></ion-icon>
                      <span>Create Portal</span>
                    </ion-button>
                  </ion-card-content>
                </ion-card>
              </div>
            </div>
          </div>

          <!-- Portal Form (Create only) -->
          <div *ngIf="isCreatingPortal && !isInitialLoading">
            <ion-card class="portal-card">
              <ion-card-header>
                <ion-card-title> Create {{ config.title }} </ion-card-title>
              </ion-card-header>
              <ion-card-content>
                <form (ngSubmit)="submitPortalForm()">
                  <div class="portal-details">
                    <div class="form-layout">
                      <div class="form-main-section">
                        <!-- Custom Domain -->
                        <div class="detail-item">
                          <ion-label class="detail-label">
                            Custom Domain
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content: tooltips.customDomain,
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </ion-label>
                          <div class="input-container">
                            <ion-input
                              class="input-field input-field-flex"
                              [(ngModel)]="portalForm.customDomain"
                              name="customDomain"
                              type="text"
                              placeholder="e.g. support.yourcompany.com"
                            ></ion-input>
                          </div>
                        </div>
                        <!-- Logo -->
                        <div class="detail-item">
                          <ion-label class="detail-label">
                            Logo
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content: tooltips.logo,
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </ion-label>
                          <div class="logo-upload-section">
                            <div class="logo-preview-container">
                              <div class="notification-icon logo-preview-box">
                                <!-- Loading state -->
                                <div
                                  *ngIf="isUploadingLogo"
                                  class="logo-loading"
                                >
                                  <ion-spinner
                                    name="crescent"
                                    class="logo-spinner"
                                  ></ion-spinner>
                                </div>
                                <!-- Upload button when no logo and not uploading -->
                                <ion-button
                                  slot="end"
                                  size="full"
                                  fill="clear"
                                  (click)="uploadPortalLogo()"
                                  class="icon-upload-button"
                                  *ngIf="
                                    !portalForm.supportLogo && !isUploadingLogo
                                  "
                                >
                                  <ion-icon
                                    name="cloud-upload-outline"
                                  ></ion-icon>
                                </ion-button>
                                <!-- Logo image when exists and not uploading -->
                                <img
                                  *ngIf="
                                    portalForm.supportLogo && !isUploadingLogo
                                  "
                                  [src]="portalForm.supportLogo"
                                  class="logo-image"
                                  (click)="uploadPortalLogo()"
                                />
                              </div>
                              <div
                                class="logo-actions"
                                *ngIf="portalForm.supportLogo"
                              >
                                <ion-button
                                  size="small"
                                  fill="clear"
                                  (click)="uploadPortalLogo()"
                                >
                                  <ion-icon
                                    name="cloud-upload-outline"
                                  ></ion-icon>
                                  <span>Upload new logo</span>
                                </ion-button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="form-side-section">
                        <!-- Primary Color -->
                        <div class="detail-item">
                          <ion-label class="detail-label">
                            Primary Color
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content: tooltips.primaryColor,
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </ion-label>
                          <div class="color-upload-section">
                            <div
                              class="color-preview-container color-preview-wrapper"
                            >
                              <div
                                class="color-preview color-preview-box"
                                [style.background-color]="
                                  portalForm.primaryColor
                                "
                              >
                                <input
                                  #colorInput
                                  type="color"
                                  [(ngModel)]="portalForm.primaryColor"
                                  name="primaryColor"
                                  class="color-input"
                                />
                              </div>
                              <ion-button
                                size="small"
                                fill="clear"
                                (click)="colorInput.click()"
                                class="color-button"
                              >
                                <ion-icon
                                  name="color-palette-outline"
                                ></ion-icon>
                                <span>Change primary color</span>
                              </ion-button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- Projects -->
                    <div class="detail-item">
                      <ion-label class="detail-label">
                        Enabled Projects
                        <ion-icon
                          name="help-circle-outline"
                          class="help-icon"
                          ngxTippy
                          [tippyProps]="{
                            content: tooltips.projects,
                            placement: 'top',
                            arrow: false,
                            touch: false
                          }"
                        ></ion-icon>
                      </ion-label>
                      <div class="projects-checkbox-list projects-list">
                        <div
                          *ngFor="let project of projects"
                          class="project-checkbox-item project-item"
                        >
                          <ion-checkbox
                            [value]="project.id"
                            [checked]="
                              portalForm.selectedProjectIds.includes(project.id)
                            "
                            (ionChange)="
                              onProjectCheckboxChange(project.id, $event)
                            "
                            class="project-checkbox"
                          ></ion-checkbox>
                          <ion-label class="project-label">{{
                            project.name
                          }}</ion-label>
                        </div>
                        <div
                          *ngIf="projects.length === 0"
                          class="no-projects-message"
                        >
                          No projects available
                        </div>
                      </div>
                    </div>
                    <!-- Action Buttons -->
                    <div class="action-buttons action-buttons-top">
                      <ion-button type="submit" [disabled]="isLoading">
                        Create Portal
                        <ion-spinner
                          name="crescent"
                          [hidden]="!isLoading"
                          class="loading-button-spinner"
                        ></ion-spinner>
                      </ion-button>
                      <ion-button
                        type="button"
                        (click)="cancelPortalForm()"
                        fill="clear"
                        color="dark"
                        [disabled]="isLoading"
                      >
                        Cancel
                      </ion-button>
                    </div>
                  </div>
                </form>
              </ion-card-content>
            </ion-card>
          </div>

          <!-- Show existing table when portal exists -->
          <div *ngIf="!isInitialLoading && hasPortal && !isCreatingPortal">
            <!-- Portal Details -->
            <div *ngFor="let portal of portals" class="portal-details">
              <ion-card class="portal-card">
                <ion-card-header style="position: relative;">
                  <ion-card-title>
                    {{ config.title }}
                  </ion-card-title>
                  <ion-button
                    fill="clear"
                    color="danger"
                    (click)="deletePortal(portal.id)"
                    style="position: absolute; top: 8px; right: 8px; z-index: 10;"
                    [disabled]="isLoading || isDnsTesting || isTlsTesting || isUploadingLogo"
                  >
                    <ion-icon name="trash-outline"></ion-icon>
                  </ion-button>
                </ion-card-header>
                <ion-card-content>
                  <!-- Show edit form only for the portal being edited -->
                  <div *ngIf="isEditingPortal && editingPortalId === portal.id">
                    <form (ngSubmit)="submitPortalForm()">
                      <div class="portal-details">
                        <div class="form-layout">
                          <div class="form-main-section">
                            <!-- Custom Domain -->
                            <div class="detail-item">
                              <ion-label class="detail-label">
                                Custom Domain
                                <ion-icon
                                  name="help-circle-outline"
                                  class="help-icon"
                                  ngxTippy
                                  [tippyProps]="{
                                    content: tooltips.customDomain,
                                    placement: 'top',
                                    arrow: false,
                                    touch: false
                                  }"
                                ></ion-icon>
                              </ion-label>
                              <div class="input-container">
                                <ion-input
                                  class="input-field input-field-flex"
                                  [(ngModel)]="portalForm.customDomain"
                                  name="customDomain"
                                  type="text"
                                  placeholder="e.g. support.yourcompany.com"
                                ></ion-input>
                              </div>
                            </div>
                            <!-- Logo -->
                            <div class="detail-item">
                              <ion-label class="detail-label">
                                Logo
                                <ion-icon
                                  name="help-circle-outline"
                                  class="help-icon"
                                  ngxTippy
                                  [tippyProps]="{
                                    content: tooltips.logo,
                                    placement: 'top',
                                    arrow: false,
                                    touch: false
                                  }"
                                ></ion-icon>
                              </ion-label>
                              <div class="logo-upload-section">
                                <div class="logo-preview-container">
                                  <div
                                    class="notification-icon logo-preview-box"
                                  >
                                    <!-- Loading state -->
                                    <div
                                      *ngIf="isUploadingLogo"
                                      class="logo-loading"
                                    >
                                      <ion-spinner
                                        name="crescent"
                                        class="logo-spinner"
                                      ></ion-spinner>
                                    </div>
                                    <!-- Upload button when no logo and not uploading -->
                                    <ion-button
                                      slot="end"
                                      size="full"
                                      fill="clear"
                                      (click)="uploadPortalLogo()"
                                      class="icon-upload-button"
                                      *ngIf="
                                        !portalForm.supportLogo &&
                                        !isUploadingLogo
                                      "
                                    >
                                      <ion-icon
                                        name="cloud-upload-outline"
                                      ></ion-icon>
                                    </ion-button>
                                    <!-- Logo image when exists and not uploading -->
                                    <img
                                      *ngIf="
                                        portalForm.supportLogo &&
                                        !isUploadingLogo
                                      "
                                      [src]="portalForm.supportLogo"
                                      class="logo-image"
                                      (click)="uploadPortalLogo()"
                                    />
                                  </div>
                                  <div
                                    class="logo-actions"
                                    *ngIf="portalForm.supportLogo"
                                  >
                                    <ion-button
                                      size="small"
                                      fill="clear"
                                      (click)="uploadPortalLogo()"
                                    >
                                      <ion-icon
                                        name="cloud-upload-outline"
                                      ></ion-icon>
                                      <span>Upload new logo</span>
                                    </ion-button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div class="form-side-section">
                            <!-- Primary Color -->
                            <div class="detail-item">
                              <ion-label class="detail-label">
                                Primary Color
                                <ion-icon
                                  name="help-circle-outline"
                                  class="help-icon"
                                  ngxTippy
                                  [tippyProps]="{
                                    content: tooltips.primaryColor,
                                    placement: 'top',
                                    arrow: false,
                                    touch: false
                                  }"
                                ></ion-icon>
                              </ion-label>
                              <div class="color-upload-section">
                                <div
                                  class="color-preview-container color-preview-wrapper"
                                >
                                  <div
                                    class="color-preview color-preview-box"
                                    [style.background-color]="
                                      portalForm.primaryColor
                                    "
                                  >
                                    <input
                                      #colorInput
                                      type="color"
                                      [(ngModel)]="portalForm.primaryColor"
                                      name="primaryColor"
                                      class="color-input"
                                    />
                                  </div>
                                  <ion-button
                                    size="small"
                                    fill="clear"
                                    (click)="colorInput.click()"
                                    class="color-button"
                                  >
                                    <ion-icon
                                      name="color-palette-outline"
                                    ></ion-icon>
                                    <span>Change primary color</span>
                                  </ion-button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <!-- Projects -->
                        <div class="detail-item">
                          <ion-label class="detail-label">
                            Enabled Projects
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content: tooltips.projects,
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </ion-label>
                          <div class="projects-checkbox-list projects-list">
                            <div
                              *ngFor="let project of projects"
                              class="project-checkbox-item project-item"
                            >
                              <ion-checkbox
                                [value]="project.id"
                                [checked]="
                                  portalForm.selectedProjectIds.includes(
                                    project.id
                                  )
                                "
                                (ionChange)="
                                  onProjectCheckboxChange(project.id, $event)
                                "
                                class="project-checkbox"
                              ></ion-checkbox>
                              <ion-label class="project-label">{{
                                project.name
                              }}</ion-label>
                            </div>
                            <div
                              *ngIf="projects.length === 0"
                              class="no-projects-message"
                            >
                              No projects available
                            </div>
                          </div>
                        </div>
                        <!-- Action Buttons -->
                        <div class="action-buttons action-buttons-top">
                          <ion-button type="submit" [disabled]="isLoading">
                            Update Portal
                            <ion-spinner
                              name="crescent"
                              [hidden]="!isLoading"
                              class="loading-button-spinner"
                            ></ion-spinner>
                          </ion-button>
                          <ion-button
                            type="button"
                            (click)="cancelPortalForm()"
                            fill="clear"
                            color="dark"
                            [disabled]="isLoading"
                          >
                            Cancel
                          </ion-button>
                        </div>
                      </div>
                    </form>
                  </div>
                  <!-- Show portal details when not editing this portal -->
                  <div
                    *ngIf="!isEditingPortal || editingPortalId !== portal.id"
                  >
                    <div class="portal-list-layout">
                      <div class="portal-main-content">
                        <div class="detail-item">
                          <ion-label class="detail-label">
                            Custom Domain
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content: tooltips.customDomain,
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </ion-label>
                          <div class="input-container">
                            <p class="detail-value">
                              {{ portal.customDomain || "Not configured" }}
                            </p>
                            <!-- Live portal link -->
                            <ion-button
                              *ngIf="portal.customDomain"
                              fill="clear"
                              [href]="'https://' + portal.customDomain"
                              target="_blank"
                              class="external-link-btn external-link-button"
                            >
                              <ion-icon
                                name="open-outline"
                                [style.color]="
                                  portal.settings?.primaryColor || '#6b7280'
                                "
                              ></ion-icon>
                            </ion-button>
                            <!-- Copy custom domain button (only one) -->
                            <ion-button
                              *ngIf="portal.customDomain"
                              fill="clear"
                              (click)="
                                copyToClipboard(
                                  'https://' + portal.customDomain,
                                  'Custom domain'
                                )
                              "
                              class="external-link-btn external-link-button"
                            >
                              <ion-icon
                                name="copy-outline"
                                [style.color]="
                                  portal.settings?.primaryColor || '#6b7280'
                                "
                              ></ion-icon>
                            </ion-button>
                          </div>
                          <div class="input-container preview-url-row">
                            <a
                              [href]="generatePreviewUrl(portal)"
                              target="_blank"
                              rel="noopener"
                              class="preview-url-minimal"
                            >
                              Preview URL
                            </a>
                            <ion-button
                              fill="clear"
                              (click)="copyPreviewUrl(portal)"
                              class="external-link-btn"
                              title="Copy preview URL"
                            >
                              <ion-icon name="copy-outline"></ion-icon>
                            </ion-button>
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content:
                                  'Share this link with colleagues to preview the portal before publishing',
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </div>
                        </div>

                        <div
                          *ngIf="portal.settings?.logoUrl"
                          class="detail-item"
                        >
                          <ion-label class="detail-label">
                            Logo
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content: tooltips.logo,
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </ion-label>
                          <img
                            [src]="portal.settings.logoUrl"
                            alt="Portal Logo"
                            class="logo-thumbnail"
                            (click)="showLogoModal(portal.settings.logoUrl)"
                          />
                        </div>
                      </div>

                      <div class="portal-side-content">
                        <div class="detail-item">
                          <ion-label class="detail-label">
                            Status
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content: tooltips.status,
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </ion-label>
                          <div class="portal-actions">
                            <ion-badge
                              [color]="portal.isEnabled ? 'success' : 'warning'"
                            >
                              {{ portal.isEnabled ? "Live" : "Draft" }}
                            </ion-badge>
                          </div>
                        </div>

                        <div
                          *ngIf="portal.settings?.primaryColor"
                          class="detail-item"
                        >
                          <ion-label class="detail-label">
                            Primary Color
                            <ion-icon
                              name="help-circle-outline"
                              class="help-icon"
                              ngxTippy
                              [tippyProps]="{
                                content: tooltips.primaryColor,
                                placement: 'top',
                                arrow: false,
                                touch: false
                              }"
                            ></ion-icon>
                          </ion-label>
                          <div
                            class="color-swatch"
                            [style.background-color]="
                              portal.settings.primaryColor
                            "
                            [attr.data-color]="portal.settings.primaryColor"
                          ></div>
                        </div>
                      </div>
                    </div>

                    <div class="detail-item">
                      <ion-label class="detail-label">
                        Enabled Projects
                        <ion-icon
                          name="help-circle-outline"
                          class="help-icon"
                          ngxTippy
                          [tippyProps]="{
                            content: tooltips.projects,
                            placement: 'top',
                            arrow: false,
                            touch: false
                          }"
                        ></ion-icon>
                      </ion-label>
                      <div class="projects-preview">
                        <div
                          *ngIf="
                            portal.projectIds &&
                            portal.projectIds.length > 0
                          "
                          class="project-list"
                        >
                          <div
                            *ngFor="let projectId of portal.projectIds"
                            class="project-item"
                          >
                            <ion-badge
                              [style.background-color]="
                                portal.settings?.primaryColor
                                  ? portal.settings.primaryColor + '1A'
                                  : 'rgba(107,114,128,0.10)'
                              "
                              [style.color]="
                                portal.settings?.primaryColor || '#6b7280'
                              "
                              [style.border]="
                                '1px solid ' +
                                (portal.settings?.primaryColor || '#6b7280')
                              "
                              [style.border-radius]="'4px'"
                              [style.padding]="'4px 8px'"
                              [style.font-size]="'12px'"
                            >
                              {{ getProjectName(projectId) }}
                            </ion-badge>
                          </div>
                        </div>
                        <div
                          *ngIf="
                            !portal.projectIds ||
                            portal.projectIds.length === 0
                          "
                          class="no-projects"
                        >
                          <p class="detail-value detail-value-medium">
                            No projects enabled
                          </p>
                        </div>
                      </div>
                    </div>

                    <div class="action-buttons action-buttons-bottom">
                      <ion-button
                        fill="outline"
                        [style.--border-color]="'#9ca3af'"
                        [style.--color]="'#9ca3af'"
                        (click)="editPortal(portal)"
                        [disabled]="isLoading || isDnsTesting || isTlsTesting || isUploadingLogo"
                      >
                        <ion-icon name="create-outline"></ion-icon>
                        Edit
                      </ion-button>
                      <!-- Test DNS connection for draft portals -->
                      <ion-button
                        fill="outline"
                        color="tertiary"
                        (click)="testDnsConnection(portal)"
                        *ngIf="!portal.isEnabled && portal.customDomain"
                        [disabled]="isLoading || isDnsTesting || isTlsTesting || isUploadingLogo"
                      >
                        <ion-spinner
                          *ngIf="isDnsTesting"
                          name="crescent"
                          class="loading-button-spinner"
                        ></ion-spinner>
                        <ion-icon
                          *ngIf="!isDnsTesting"
                          name="checkmark-circle-outline"
                        ></ion-icon>
                        Test DNS
                      </ion-button>

                      <!-- Test TLS certificate for draft portals -->
                      <ion-button
                        fill="outline"
                        color="secondary"
                        (click)="testTlsCertificate(portal)"
                        *ngIf="!portal.isEnabled && portal.customDomain"
                        [disabled]="isLoading || isDnsTesting || isTlsTesting || isUploadingLogo"
                      >
                        <ion-spinner
                          *ngIf="isTlsTesting"
                          name="crescent"
                          class="loading-button-spinner"
                        ></ion-spinner>
                        <ion-icon
                          *ngIf="!isTlsTesting"
                          name="shield-checkmark-outline"
                        ></ion-icon>
                        Test TLS
                      </ion-button>


                      <ion-button
                        fill="outline"
                        color="success"
                        (click)="publishPortal(portal.id)"
                        *ngIf="!portal.isEnabled"
                        [disabled]="isLoading || isDnsTesting || isTlsTesting || isUploadingLogo"
                      >
                        <ion-spinner *ngIf="isLoading" name="crescent" class="loading-button-spinner"></ion-spinner>
                        <ion-icon name="play-circle-outline" *ngIf="!isLoading"></ion-icon>
                        Publish
                      </ion-button>
                      <ion-button
                        fill="outline"
                        color="warning"
                        (click)="disablePortal(portal.id)"
                        *ngIf="portal.isEnabled"
                        class="disable-btn"
                        [disabled]="isLoading || isDnsTesting || isTlsTesting || isUploadingLogo"
                      >
                        <ion-icon name="pause-circle-outline"></ion-icon>
                        Disable
                      </ion-button>
                    </div>

                    <!-- DNS Test Steps Display -->
                    <div
                      *ngIf="
                        isDnsTesting && currentTestingPortal?.id === portal.id
                      "
                      class="dns-test-steps"
                      style="margin-top: 16px; max-width: 400px; margin-left: auto; margin-right: auto;"
                    >
                      <div class="test-steps-container">
                        <div
                          *ngFor="let step of dnsTestSteps; let i = index"
                          class="test-step"
                          [class.active]="i === currentDnsStep"
                          [class.completed]="i < currentDnsStep"
                        >
                          <div class="step-icon">
                            <ion-icon
                              *ngIf="i < currentDnsStep"
                              name="checkmark-circle"
                              color="success"
                            ></ion-icon>
                            <ion-icon
                              *ngIf="i === currentDnsStep"
                              name="ellipse"
                              color="primary"
                            ></ion-icon>
                            <ion-icon
                              *ngIf="i > currentDnsStep"
                              name="ellipse-outline"
                              color="medium"
                            ></ion-icon>
                          </div>
                          <div class="step-text">{{ step }}</div>
                        </div>
                      </div>
                    </div>

                    <!-- TLS Test Steps Display -->
                    <div
                      *ngIf="
                        isTlsTesting && currentTestingPortal?.id === portal.id
                      "
                      class="dns-test-steps"
                      style="margin-top: 16px; max-width: 400px; margin-left: auto; margin-right: auto;"
                    >
                      <div class="test-steps-container">
                        <div
                          *ngFor="let step of tlsTestSteps; let i = index"
                          class="test-step"
                          [class.active]="i === currentTlsStep"
                          [class.completed]="i < currentTlsStep"
                        >
                          <div class="step-icon">
                            <ion-icon
                              *ngIf="i < currentTlsStep"
                              name="checkmark-circle"
                              color="success"
                            ></ion-icon>
                            <ion-icon
                              *ngIf="i === currentTlsStep"
                              name="ellipse"
                              color="primary"
                            ></ion-icon>
                            <ion-icon
                              *ngIf="i > currentTlsStep"
                              name="ellipse-outline"
                              color="medium"
                            ></ion-icon>
                          </div>
                          <div class="step-text">{{ step }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </ion-card-content>
              </ion-card>
            </div>
          </div>
        </ion-col>
      </ion-row>
    </ion-grid>
  </div>

  <!-- Bottom padding -->
  <div class="footer-spacer-small"></div>
</ion-content>
